/**
 * Shared site-parcel vs infrastructure classification for KMZ import and
 * persisted-site reconciliation on read.
 */

import type { SiteLayerCategory } from '../types/site';

/** Current aggregate schema — union totalArea + overlap flag. */
export const SITE_BOUNDARY_AGGREGATE_VERSION = 2;

/** Overlap threshold (acres) before flagging hasOverlappingParcels. */
export const OVERLAP_AREA_THRESHOLD_ACRES = 0.5;

/** Roman-numeral or numeric phase labels, e.g. "Phase I", "Phase 2". */
export function isPhaseSiteParcelName(name: string): boolean {
  if (!/\bphase\s+(?:i{1,3}|iv|v|vi{0,3}|[0-9]+)/i.test(name)) return false;
  if (/\b(expansion|setback|planned|option)\b/i.test(name)) return false;
  return true;
}

/** Site labels with numeric or Roman-numeral ids — "Site 1", "Site I", "Site II". */
export const SITE_NUMBER_OR_PARCEL = /\bsite\s*(?:\d+|i{1,3}|iv|vi{0,3})\b|\bparcel\b/i;

export function isSiteNumberOrParcelName(name: string): boolean {
  return SITE_NUMBER_OR_PARCEL.test(name.trim());
}

/**
 * Infrastructure / planned overlays — must NOT become site boundary parcels.
 */
export function isInfrastructureParcelName(name: string, folderPath: string[] = []): boolean {
  const n = name.trim().toLowerCase();
  const folderText = folderPath.join(' ').toLowerCase();

  if (/sub\s*station|substation/i.test(n)) return true;
  if (/power\s*plant|generating\s*station|switchyard/i.test(n)) return true;
  if (/setback|wetland|flood/i.test(n)) return true;
  if (/\bplanned\b/i.test(n) && !isPhaseSiteParcelName(name) && !isSiteNumberOrParcelName(name)) {
    return true;
  }
  if (/\bplanned\b/i.test(n) && /substation|utility|power|plant|switchyard/i.test(n)) return true;

  if (
    /(expansion|planned|setback|constraint)/i.test(folderText)
    && !isPhaseSiteParcelName(name)
    && !isSiteNumberOrParcelName(name)
  ) {
    return true;
  }
  return false;
}

/**
 * Site-parcel polygons (phases, site boundaries, parcels) become boundary
 * candidates. Infrastructure overlays are layers.
 */
export function isSiteParcelName(name: string, folderPath: string[] = []): boolean {
  if (isInfrastructureParcelName(name, folderPath)) return false;
  const n = name.trim();
  if (/^\s*site\s*boundary\s*$/i.test(n)) return true;
  if (isPhaseSiteParcelName(n)) return true;
  if (isSiteNumberOrParcelName(n)) return true;
  if (/boundary/i.test(n) && !/substation/i.test(n)) return true;
  const folderText = folderPath.join(' ').toLowerCase();
  if (/site|boundary/i.test(folderText) && !/(expansion|constraint|substation|pipeline)/i.test(folderText)) {
    return true;
  }
  // Unclassified closed polygons default to boundary candidates (legacy KMZs).
  return true;
}

/**
 * Feature #95 — recognise a KMZ folder/placemark name as a POWER-GEN area
 * (where prime-mover / generation BOP components belong: turbines,
 * generators, reactors, the powerblock). Additive, read by `fromKmz.ts`
 * for intelligent BOP placement. Does NOT alter any existing
 * classification return value.
 *
 * Matches tokens like: "power gen", "power generation", "powerblock",
 * "power island", "generation area", "power plant", "generating station",
 * "turbine hall", "powerhouse".
 */
export function isPowerGenAreaName(name: string, folderPath: string[] = []): boolean {
  const haystack = `${folderPath.join(' ')} ${name}`.toLowerCase();
  if (haystack.trim() === '') return false;
  if (/power\s*(?:gen(?:eration)?|block|island|house)/.test(haystack)) return true;
  if (/generation\s*area/.test(haystack)) return true;
  if (/power\s*plant|generating\s*station/.test(haystack)) return true;
  if (/turbine\s*(?:hall|building|area)/.test(haystack)) return true;
  return false;
}

/**
 * Feature #95 — recognise a KMZ folder/placemark name as a BALANCE-OF-PLANT
 * (BOP) area (cooling, water treatment, switchyard, auxiliaries — where
 * pumps / heat exchangers / cooling towers belong). Additive, read by
 * `fromKmz.ts`. Power-gen areas (above) also qualify as BOP-eligible since
 * a powerblock can host BOP equipment when no dedicated BOP area exists.
 *
 * Matches tokens like: "BOP", "balance of plant", "plant area", "cooling
 * area", "switchyard", "substation", "utility area", "auxiliary".
 */
export function isBopAreaName(name: string, folderPath: string[] = []): boolean {
  const haystack = `${folderPath.join(' ')} ${name}`.toLowerCase();
  if (haystack.trim() === '') return false;
  if (/\bbop\b/.test(haystack)) return true;
  if (/balance\s*of\s*plant/.test(haystack)) return true;
  if (/plant\s*area/.test(haystack)) return true;
  if (/cooling\s*(?:area|yard|tower\s*area)/.test(haystack)) return true;
  if (/switch\s*yard|switchyard/.test(haystack)) return true;
  if (/sub\s*station|substation/.test(haystack)) return true;
  if (/utility\s*area|auxiliary|aux\s*area/.test(haystack)) return true;
  // A power-gen area can host BOP equipment as a fallback.
  return isPowerGenAreaName(name, folderPath);
}

/** Layer category from folder path + placemark name (first-match-wins). */
export function classifyLayerCategory(name: string, folderPath: string[]): SiteLayerCategory {
  const haystack = `${folderPath.join(' ')} ${name}`.toLowerCase();
  if (haystack.trim() === '') return 'other';
  if (/(pipeline|gas|oil|co2|lpg)/.test(haystack)) return 'infrastructure.pipeline';
  if (/(electric|transmission|substation|sub\s*station|kv|switchyard|power\s*plant)/.test(haystack)) {
    return 'infrastructure.electrical';
  }
  if (/(fiber|connectivity|network)/.test(haystack)) return 'infrastructure.fiber';
  if (/(flood|wetland|setback|constraint)/.test(haystack)) return 'constraint.environmental';
  if (/(access|road|route)/.test(haystack)) return 'infrastructure.transport';
  if (/(expansion|planned|phased|option)/.test(haystack)) return 'concept.expansion';
  return 'other';
}
